import { useMutation, useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { ChevronRight, KeyRound, Plus } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { trpc, type RouterOutputs } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { PageFrame } from 'shared/components/page-frame'
import { CompactTable, EmptyTableBody, SectionTitle } from 'shared/components/section'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from 'shared/components/ui/sheet'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { type IssuedB2bKey, PartnerConnection } from './PartnerConnection'

type Partner = RouterOutputs['partners']['List'][number]

/** A secret just issued — shown once, in the dialog, and never again. */
type Issued = { kind: 'apiKey'; secret: string } | ({ kind: 'b2bKey' } & IssuedB2bKey)

/**
 * Partners, their two secrets and their Connection — the "partners and policies"
 * section. A partner has exactly two secrets: the API key it calls TezGo with, and
 * the B2B key TezGo signs its facts with on the partner's webhook (ticket 37). Each
 * is shown once, at issue.
 *
 * The partners are one table: name, prefix, whether TezGo sends them facts. A click
 * on a row opens the partner's keys and Connection in a side panel — set up once,
 * so out of sight until asked for. «Новый партнёр» is a dialog; revoking an API key
 * asks first.
 */
export function Partners() {
	const { t } = useTranslate()
	const [creating, setCreating] = useState(false)
	const [openId, setOpenId] = useState<string | null>(null)
	const [issued, setIssued] = useState<Issued | null>(null)

	const partnersQuery = useQuery(trpc.partners.List.queryOptions())
	const partners = partnersQuery.data ?? []
	const open = partners.find((p) => p._id === openId) ?? null

	const columns: ColumnDef<Partner>[] = [
		{ id: 'name', header: t('partners.colName'), cell: ({ row }) => <span className='font-medium'>{row.original.name}</span> },
		{ id: 'prefix', header: t('partners.colPrefix'), cell: ({ row }) => <span className='font-mono'>{row.original.prefix}</span> },
		{
			id: 'connection',
			header: t('partners.colConnection'),
			cell: ({ row: { original: p } }) => <ConnectionBadge partner={p} />,
		},
		{
			id: 'webhook',
			header: t('partners.webhookLabel'),
			cell: ({ row }) =>
				row.original.connection.webhookUrl ? (
					<span className='block max-w-80 truncate font-mono text-xs text-muted-foreground'>
						{row.original.connection.webhookUrl}
					</span>
				) : (
					<span className='text-muted-foreground'>—</span>
				),
		},
		{
			id: 'open',
			header: '',
			meta: { cellClassName: 'w-px text-muted-foreground' },
			cell: () => <ChevronRight className='size-4' />,
		},
	]

	const { table } = useDataTable({
		data: partners,
		columns,
		rowCount: partners.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (p) => p._id,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})

	return (
		<PageFrame
			title='partners.title'
			actions={
				<Button data-testid='partner-new' onClick={() => setCreating(true)}>
					<Plus />
					{t('partners.new')}
				</Button>
			}
		>
			{partnersQuery.isError ? (
				<p className='text-sm text-destructive'>{partnersQuery.error.message}</p>
			) : partnersQuery.isLoading ? (
				<p className='text-sm text-muted-foreground'>{t('partners.loading')}</p>
			) : (
				<div data-testid='partners-table'>
					<DataTable
						table={table}
						withPagination={false}
						emptyText={t('partners.empty')}
						row={{
							attributes: (p) => ({ 'data-testid': `partner-row-${p.prefix}` }),
							onRowClick: (p) => setOpenId(p._id),
						}}
					/>
				</div>
			)}

			<CreatePartnerDialog open={creating} onOpenChange={setCreating} onCreated={() => partnersQuery.refetch()} />

			<Sheet open={open !== null} onOpenChange={(o) => !o && setOpenId(null)}>
				<SheetContent className='overflow-y-auto data-[side=right]:sm:max-w-xl'>
					{open && <PartnerSheet partner={open} onIssued={setIssued} />}
				</SheetContent>
			</Sheet>

			<Dialog open={issued !== null} onOpenChange={(o) => !o && setIssued(null)}>
				<DialogContent data-testid='partner-issued'>
					<DialogHeader>
						<DialogTitle>
							{issued?.kind === 'b2bKey' ? t('partners.b2bKeyIssuedTitle') : t('partners.keyIssuedTitle')}
						</DialogTitle>
						<DialogDescription>
							{issued?.kind === 'b2bKey' ? t('partners.b2bKeyIssuedBody') : t('partners.keyIssuedBody')}
						</DialogDescription>
					</DialogHeader>
					{issued?.kind === 'b2bKey' && (
						<div className='grid gap-1 text-sm'>
							<span className='text-muted-foreground'>{t('partners.b2bKeyId')}</span>
							<code className='block rounded-md bg-muted p-3 text-sm break-all' data-testid='partner-issued-key-id'>
								{issued.keyId}
							</code>
							<span className='text-muted-foreground'>{t('partners.b2bSecret')}</span>
						</div>
					)}
					<code className='block rounded-md bg-muted p-3 text-sm break-all' data-testid='partner-issued-secret'>
						{issued?.secret}
					</code>
				</DialogContent>
			</Dialog>
		</PageFrame>
	)
}

/** Whether TezGo sends the partner its facts: a B2B key and a webhook, a key alone, or nothing. */
function ConnectionBadge({ partner }: { partner: Partner }) {
	const { t } = useTranslate()
	const { b2bKey, webhookUrl } = partner.connection
	if (b2bKey && webhookUrl) return <Badge variant='outline'>{t('partners.connected')}</Badge>
	if (b2bKey) return <Badge variant='secondary'>{t('partners.keyOnly')}</Badge>
	return <span className='text-muted-foreground'>{t('partners.notConnected')}</span>
}

function CreatePartnerDialog({
	open,
	onOpenChange,
	onCreated,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	onCreated: () => void
}) {
	const { t } = useTranslate()
	const [name, setName] = useState('')
	const [prefix, setPrefix] = useState('')

	const createMutation = useMutation({
		...trpc.partners.Create.mutationOptions(),
		onSuccess: () => {
			setName('')
			setPrefix('')
			onOpenChange(false)
			onCreated()
			toast.success(t('partners.created'))
		},
		onError: (error) => toast.error(error.message),
	})

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault()
		createMutation.mutate({ name, prefix })
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='sm:max-w-md' aria-describedby={undefined} data-testid='partner-create-dialog'>
				<DialogHeader>
					<DialogTitle>{t('partners.new')}</DialogTitle>
				</DialogHeader>
				<form className='grid gap-4' onSubmit={handleSubmit}>
					<div className='grid gap-1.5'>
						<Label htmlFor='partner-name'>{t('partners.colName')}</Label>
						<Input id='partner-name' value={name} onChange={(e) => setName(e.target.value)} required />
					</div>
					<div className='grid gap-1.5'>
						<Label htmlFor='partner-prefix'>{t('partners.colPrefix')}</Label>
						<Input
							id='partner-prefix'
							className='w-40 font-mono'
							value={prefix}
							onChange={(e) => setPrefix(e.target.value.toUpperCase())}
							placeholder={t('partners.prefixPlaceholder')}
							required
						/>
					</div>
					<DialogFooter>
						<Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
							{t('partners.cancel')}
						</Button>
						<Button disabled={createMutation.isPending || !name.trim() || !prefix.trim()} data-testid='partner-create'>
							{t('partners.create')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}

/** One partner's side panel: its API keys and its Connection (the B2B key, the webhook). */
function PartnerSheet({ partner, onIssued }: { partner: Partner; onIssued: (issued: Issued) => void }) {
	const { t } = useTranslate()
	const [revoking, setRevoking] = useState<string | null>(null)
	const keysQuery = useQuery(trpc.partners.ListApiKeys.queryOptions({ partnerId: partner._id }))
	const keys = keysQuery.data ?? []

	const issueMutation = useMutation({
		...trpc.partners.IssueApiKey.mutationOptions(),
		onSuccess: async (data) => {
			onIssued({ kind: 'apiKey', secret: data.secret })
			await keysQuery.refetch()
		},
		onError: (error) => toast.error(error.message),
	})

	const revokeMutation = useMutation({
		...trpc.partners.RevokeApiKey.mutationOptions(),
		onSuccess: async () => {
			setRevoking(null)
			await keysQuery.refetch()
			toast.success(t('partners.revoked'))
		},
		onError: (error) => toast.error(error.message),
	})

	return (
		<div className='flex flex-col gap-6 pb-6' data-testid={`partner-${partner.prefix}`}>
			<SheetHeader className='pb-0'>
				<SheetTitle className='text-lg'>{partner.name}</SheetTitle>
				<SheetDescription>{t('partners.prefixLabel', { prefix: partner.prefix })}</SheetDescription>
			</SheetHeader>

			<section className='flex flex-col gap-2 px-4'>
				<SectionTitle
					title={t('partners.apiKeyTitle')}
					hint={t('partners.apiKeyHint')}
					aside={
						<Button
							variant='outline'
							size='sm'
							disabled={issueMutation.isPending}
							onClick={() => issueMutation.mutate({ partnerId: partner._id })}
							data-testid='partner-api-issue'
						>
							<KeyRound />
							{t('partners.issueKey')}
						</Button>
					}
				/>
				<CompactTable testId='partner-api-keys'>
					<TableHeader>
						<TableRow>
							<TableHead>{t('partners.colKey')}</TableHead>
							<TableHead>{t('partners.colStatus')}</TableHead>
							<TableHead />
						</TableRow>
					</TableHeader>
					{keys.length === 0 ? (
						<EmptyTableBody columns={3}>{t('partners.noKeys')}</EmptyTableBody>
					) : (
						<TableBody>
							{keys.map((key) => (
								<TableRow key={key._id}>
									<TableCell className='font-mono'>{key.preview}</TableCell>
									<TableCell>
										{key.revoked ? (
											<Badge variant='secondary'>{t('partners.keyRevoked')}</Badge>
										) : (
											<Badge variant='outline'>{t('partners.keyActive')}</Badge>
										)}
									</TableCell>
									<TableCell className='w-px text-right'>
										{!key.revoked && (
											<Button
												variant='ghost'
												size='sm'
												title={t('partners.revoke')}
												onClick={() => setRevoking(key._id)}
											>
												{t('partners.revoke')}
											</Button>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					)}
				</CompactTable>
			</section>

			<PartnerConnection partner={partner} onB2bKeyIssued={(key) => onIssued({ kind: 'b2bKey', ...key })} />

			<AlertDialog open={revoking !== null} onOpenChange={(o) => !o && !revokeMutation.isPending && setRevoking(null)}>
				<AlertDialogContent data-testid='partner-revoke-dialog'>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('partners.revokeAsk')}</AlertDialogTitle>
						<AlertDialogDescription>{t('partners.revokeHint')}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={revokeMutation.isPending}>{t('partners.cancel')}</AlertDialogCancel>
						<Button
							variant='destructive'
							disabled={revokeMutation.isPending}
							onClick={() => revoking && revokeMutation.mutate({ id: revoking })}
							data-testid='partner-revoke-confirm'
						>
							{t('partners.revoke')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
